<template>
  <div class="md-layout page-reviewer-calendar">
    <div
      class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-100"
    >
      <md-toolbar class="md-primary">
        <h3 class="md-title">
          {{ $t("Calendar") }}
        </h3>
      </md-toolbar>
      <br />
    </div>

    <div class="md-layout-item">
      <div style="font-size: 12px; text-align: right;">
        ({{ $t("Timezone") }}:
        {{ calendarOptions && calendarOptions.timeZone }})
      </div>
      <FullCalendar :options="calendarOptions">
        <template v-slot:eventContent="arg">
          <b>{{ arg.timeText }}</b>
          <i>{{ arg.event.title }}</i>
        </template>
      </FullCalendar>
    </div>
    <md-dialog :md-active.sync="showDialog">
      <md-dialog-title>{{ $t("Client Information") }}</md-dialog-title>

      <div class="md-dialog-content">
        <div>
          {{ $t("Package") }}:
          <b>{{ selectedBookInfo.package_title }}</b>
        </div>
        <div>
          {{ $t("Client Name") }}:
          <b>{{ selectedBookInfo.customer_name }}</b>
        </div>
        <div>
          {{ $t("Client Phone") }}:
          <b>
            <a
              v-if="selectedBookInfo.customer_phone"
              class="link"
              target="_blank"
              :href="`tel: ${selectedBookInfo.customer_phone}`"
            >
              {{ selectedBookInfo.customer_phone }}
            </a>
          </b>
          <span
            v-if="selectedBookInfo.customer_phone"
            v-clipboard="selectedBookInfo.customer_phone"
            class="material-icons copy-icon"
            v-clipboard:success="clipboardCbfn"
            style="top: 0;"
          >
            content_copy
          </span>
        </div>
        <div>
          {{ $t("Client Email") }}:
          <b>
            <a
              v-if="selectedBookInfo.customer_email"
              class="link"
              target="_blank"
              :href="`mailto: ${selectedBookInfo.customer_email}`"
            >
              {{ selectedBookInfo.customer_email }}
            </a>
          </b>
          <span
            v-if="selectedBookInfo.customer_email"
            v-clipboard="selectedBookInfo.customer_email"
            class="material-icons copy-icon"
            v-clipboard:success="clipboardCbfn"
            style="top: 0;"
          >
            content_copy
          </span>
        </div>
      </div>

      <md-dialog-actions>
        <md-button class="md-primary" @click="showDialog = false">{{
          $t("Close")
        }}</md-button>
      </md-dialog-actions>
    </md-dialog>
    <Toast ref="toast"></Toast>
  </div>
</template>

<script>
import FullCalendar from "@fullcalendar/vue";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import esLocale from "@fullcalendar/core/locales/es";
import frLocale from "@fullcalendar/core/locales/fr";
import momentTimezonePlugin from "@fullcalendar/moment-timezone";
import { getReviewerAvailableSchedule } from "@/services/flightReview.service.js";
import { DEFAULT_TIMEZONE } from "@/const.js";
import Toast from "@/components/Toast.vue";
import { secondsToHoursAndMinutes, generateEvents } from "@/helpers/utils";
import swal from "sweetalert2";

export default {
  components: {
    FullCalendar, // make the <FullCalendar> tag available
    Toast
  },

  data: function() {
    const locale = window.localStorage.getItem("locale") || "en";
    return {
      calendarOptions: {
        locales: [esLocale, frLocale],
        locale,
        plugins: [
          dayGridPlugin,
          timeGridPlugin,
          interactionPlugin, // needed for dateClick
          momentTimezonePlugin
        ],
        headerToolbar: {
          left: "prev,next today",
          center: "title",
          right: "dayGridMonth,timeGridWeek,timeGridDay"
        },
        initialView: "timeGridWeek",
        timeZone: DEFAULT_TIMEZONE,
        events: [],
        editable: false,
        selectable: true,
        selectMirror: true,
        dayMaxEvents: true,
        weekends: true,
        eventClick: this.handleEventClick,
        displayEventTime: false
      },
      showDialog: false,
      selectedBookInfo: {}
    };
  },

  mounted() {
    this.initCalendarOptions();
  },

  methods: {
    async initCalendarOptions() {
      const {
        events,
        schedule = [],
        review_timezone,
        always_available
      } = await getReviewerAvailableSchedule();
      if (always_available) {
        this.calendarOptions.timeZone = timezone;
        swal.fire({
          text: this.$t("You've enabled 'Contact to schedule' option."),
          confirmButtonClass: "md-button md-primary",
          buttonsStyling: false
        });
        return;
      }
      let timezone = review_timezone || DEFAULT_TIMEZONE;
      let futureAvailableAll = [],
        pastBookAll = [],
        futureBookAll = [];
      for (let item of schedule) {
        let startTime, endTime;
        const availableSchedule = item.available_schedule;
        if (availableSchedule) {
          let weeklyIdx = [];
          delete availableSchedule.repeat;
          for (let key in availableSchedule) {
            let _availableSchedule = availableSchedule[key];
            if (_availableSchedule.available) {
              startTime = secondsToHoursAndMinutes(
                _availableSchedule.start_time
              );
              endTime = secondsToHoursAndMinutes(_availableSchedule.end_time);
              weeklyIdx.push(key);
            }
          }
          const { futureAvailable, pastBook, futureBook } = generateEvents({
            daysOfWeek: weeklyIdx,
            startTime,
            endTime,
            startDate: item.start_time.$date,
            endDate: item.end_time.$date,
            events,
            timezone,
            duration: item.duration || 60
          });
          futureAvailableAll = futureAvailableAll.concat(futureAvailable);
          pastBookAll = pastBook;
          futureBookAll = futureBook;
        }
      }
      this.calendarOptions.timeZone = timezone;
      this.calendarOptions.events = [
        ...futureAvailableAll,
        ...pastBookAll,
        ...futureBookAll
      ];
    },
    handleWeekendsToggle() {
      this.calendarOptions.weekends = !this.calendarOptions.weekends; // update a property
    },

    handleDateSelect(selectInfo) {
      let title = prompt("Please enter a new title for your event");
      let calendarApi = selectInfo.view.calendar;

      calendarApi.unselect(); // clear date selection

      if (title) {
        calendarApi.addEvent({
          id: +new Date(),
          title,
          start: selectInfo.startStr,
          end: selectInfo.endStr,
          allDay: selectInfo.allDay
        });
      }
    },

    handleEventClick(clickInfo) {
      if (
        clickInfo.el.classList.contains("booked-future") ||
        clickInfo.el.classList.contains("booked-past")
      ) {
        let bookedInfo = clickInfo.event._def.extendedProps.eventInfo;
        if (bookedInfo) {
          console.log(bookedInfo);
          this.selectedBookInfo = bookedInfo;
          this.showDialog = true;
        }
      }
    },
    clipboardCbfn() {
      this.$refs.toast.showToast(this.$t("Copied"));
    }
  }
};
</script>

<style lang="css">
.page-reviewer-calendar {
  .booked-past {
    background-color: #ccc;
  }
  .booked-future {
    background-color: #3788d8;
    cursor: pointer;
  }
  .available-event {
    background-color: #4caf50;
  }
}
</style>
